import { Locale } from '@core/constants/locales.constants';
import { ARTICLE_FORMATS } from '@core/types/pages/articlePage.types';
import { HeaderBlocksHero } from '@graphql/index';
import { Translations } from '@lib/dictionary-keys/__generated__';
import {
  ArticleType,
  Flex,
  HeroHeaderBlockProps,
  ThumbnailCardProps,
} from '@stadionhq-v3/base-ui';
import { CleanContentful } from '@stadionhq-v3/clean-contentful';
import { ContentfulImage } from '@stadionhq-v3/contentful-ui';
import {
  ArrowRight,
  Article,
  ChevronLeft,
  ChevronRight,
  Gallery,
  Lock,
  Pause,
  Play,
  Podcast,
} from '@components/icons';
import templateConfig from '@templateConfig';
import { generateFullPathname } from '@utils/paths/generateFullPathname.utils';
import { Button } from '@components/v3/atoms/button';
import { linkTransform } from '../atoms';

const getCardIcon = (articleType: ArticleType) => {
  switch (articleType) {
    case ARTICLE_FORMATS.NEWS:
      return <Article />;
    case ARTICLE_FORMATS.GALLERY:
      return <Gallery />;
    case ARTICLE_FORMATS.VIDEO:
      return <Play />;
    case ARTICLE_FORMATS.PODCAST:
      return <Podcast />;
    default:
      console.error('articleType not supported:', `'${articleType}'`);
      return undefined;
  }
};

const heroHeaderBlockTransform = (
  { slideItems }: CleanContentful<HeaderBlocksHero>,
  locale: Locale,
  dictionary: {
    readNewsArticle: Translations['news']['label']['readNewsArticle'];
    viewGalleryArticle: Translations['news']['label']['viewGalleryArticle'];
    watchVideoArticle: Translations['news']['label']['watchVideoArticle'];
    sponsoredBy: Translations['common']['label']['sponsoredBy'];
    previousAction: Translations['common']['action']['previous'];
    nextAction: Translations['common']['action']['next'];
    membersOnly: Translations['articleCard']['label']['membersOnly'];
  }
): HeroHeaderBlockProps => ({
  heroItems: slideItems?.map((article, index) => {
    const ctaLabel = (format: ArticleType) => {
      switch (format) {
        case ARTICLE_FORMATS.NEWS: {
          return dictionary.readNewsArticle;
        }
        case ARTICLE_FORMATS.VIDEO: {
          return dictionary.watchVideoArticle;
        }
        case ARTICLE_FORMATS.GALLERY: {
          return dictionary.viewGalleryArticle;
        }
        default: {
          return dictionary.readNewsArticle;
        }
      }
    };

    const isArticle = article.__typename === 'ArticlePage';
    const isItemHero = article.__typename === 'ItemHeroSlide';

    const primaryLink =
      isItemHero &&
      article.primaryLink &&
      article.primaryLink.__typename === 'Link' &&
      linkTransform(article.primaryLink, locale);

    const secondaryLink =
      isItemHero &&
      article.secondaryLink &&
      article.secondaryLink.__typename === 'Link' &&
      linkTransform(article.secondaryLink, locale);

    const tertiaryLink =
      isItemHero &&
      article.tertiaryLink &&
      article.tertiaryLink.__typename === 'Link' &&
      linkTransform(article.tertiaryLink, locale);

    const cardIcon = isArticle && getCardIcon(article.format as ArticleType);

    return {
      id: article.id,
      counter: `0${index + 1}`,
      preTitleText: isItemHero && article.slideSectionPreTitleText,
      title: isArticle ? article.articleTitle : isItemHero && article.title,
      cardIcon: cardIcon ? { icon: cardIcon } : undefined,
      publishDate: isArticle && {
        date: article.publishDate,
        format: 'DD MMM YY',
      },
      category: isArticle
        ? article?.category && {
            title: article?.category.category || '',
            category: article?.category.category || '',
            slug: generateFullPathname({
              locale,
              route: templateConfig.navigation.routes.NEWS_CATEGORY,
              params: { category: article.category.slug },
            }),
          }
        : isItemHero && {
            title: article.slideSectionPreTitleText || '',
            category: article.slideSectionPreTitleText || '',
            slug: '#',
            disabled: true,
          },
      sponsor: isArticle &&
        article.sponsor && {
          sponsorLabel: dictionary.sponsoredBy,
          onDark: true,
          image: (
            <ContentfulImage
              height={article.sponsor?.height || 0}
              width={article.sponsor?.width || 0}
              url={article.sponsor?.url || ''}
              alt={article.sponsor?.title || ''}
            />
          ),
        },
      membersBadge: isArticle &&
        article.isRestrictedToMembersOnly && {
          leftElement: <Lock />,
          text: dictionary.membersOnly,
        },
      image: article?.image ? (
        <ContentfulImage {...article.image} priority />
      ) : (
        <ContentfulImage
          alt="Placeholder Image"
          width={420}
          height={360}
          url="https://images.ctfassets.net/qpz2vl40uleh/5t4ugdlfydzHls9NEfUAn5/80283eedacf3c4a8ef85a44a5209d2b6/y9DpT.jpeg"
          priority
        />
      ),
      ctaButton: isArticle ? (
        <Button
          {...{
            text: ctaLabel(article.format as ArticleType),
            url: generateFullPathname({
              locale,
              route: templateConfig.navigation.routes.ARTICLE,
              params: { article: article?.slug },
            }),
            variant: 'text-button',
            onDark: true,
            iconAfter: <ArrowRight />,
          }}
        />
      ) : (
        isItemHero && (
          <Flex
            flexDirection="row"
            flexWrap="wrap"
            flexGrow={1}
            tokens={{
              gap: '--s-spacer-6',
            }}
          >
            {primaryLink && (
              <Button
                {...{
                  type: 'button',
                  text: primaryLink.title,
                  url: primaryLink.url,
                  target: primaryLink.target,
                  openInNewTab: primaryLink.openInNewTab,
                  variant: 'text-button',
                  onDark: true,
                  iconAfter: <ArrowRight />,
                }}
              />
            )}
            {secondaryLink && (
              <Button
                {...{
                  type: 'button',
                  text: secondaryLink.title,
                  url: secondaryLink.url,
                  target: secondaryLink.target,
                  openInNewTab: secondaryLink.openInNewTab,
                  variant: 'text-button',
                  onDark: true,
                  iconAfter: <ArrowRight />,
                }}
              />
            )}
            {tertiaryLink && (
              <Button
                {...{
                  type: 'button',
                  text: tertiaryLink.title,
                  url: tertiaryLink.url,
                  target: tertiaryLink.target,
                  openInNewTab: tertiaryLink.openInNewTab,
                  variant: 'text-button',
                  onDark: true,
                  iconAfter: <ArrowRight />,
                }}
              />
            )}
          </Flex>
        )
      ),
    };
  }) as ThumbnailCardProps[],
  heroPagination: {
    interactive: true,
  },
  playPauseButton: {
    playIcon: <Play />,
    playText: 'Play',
    pauseText: 'Pause',
    pauseIcon: <Pause />,
  },
  previousButton: {
    text: dictionary.previousAction,
    iconBefore: <ChevronLeft />,
  },
  nextButton: {
    text: dictionary.nextAction,
    iconBefore: <ChevronRight />,
  },
});

export default heroHeaderBlockTransform;
